import { useCallback, useState, useEffect, useRef } from 'react';
import { notify } from '@/lib/notify';
import type { GetChatsOptions, TelegramChat, PublicMessage, ChatType } from '../types';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

interface UseTelegramChatsOptions extends Partial<GetChatsOptions> {
  /** Фильтр по типу чата */
  chatType?: ChatType;
}

const CHATS_PER_PAGE = 50;

export function useTelegramChats(options: UseTelegramChatsOptions = {}) {
  const [chats, setChats] = useState<TelegramChat[]>([]);
  const [publicMessages, setPublicMessages] = useState<PublicMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [isPublicLoading, setIsPublicLoading] = useState(false);
  const [isLinking, setIsLinking] = useState(false);

  // Ref для предотвращения гонки условий при infinite scroll
  const isLoadingMoreRef = useRef(false);
  const hasMoreRef = useRef(true);
  const chatsLengthRef = useRef(0);

  // Синхронизируем refs со state
  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  useEffect(() => {
    chatsLengthRef.current = chats.length;
  }, [chats.length]);

  const fetchChats = useCallback(async () => {
    setIsLoading(true);
    setHasMore(true);
    hasMoreRef.current = true;
    isLoadingMoreRef.current = false;
    try {
      const limit = options.limit ?? CHATS_PER_PAGE;
      const queryString = new URLSearchParams({
        limit: String(limit),
        offset: String(options.offset ?? 0),
        ...(options.search && { search: options.search }),
        ...(options.linkedToOrder !== undefined && { linkedToOrder: String(options.linkedToOrder) }),
        ...(options.chatType && { chatType: options.chatType }),
      }).toString();

      const response = await fetch(`${API_URL}/api/telegram-client/chats?${queryString}`);

      const result: ApiResponse<TelegramChat[]> = await response.json();
      if (result.success && result.data) {
        // Фильтруем на клиенте если бэкенд не поддерживает chatType
        const filteredChats = options.chatType
          ? result.data?.filter(c => c.chatType === options.chatType) ?? []
          : result.data ?? [];
        setChats(filteredChats);
        // Если получили меньше чем запрашивали — больше нет чатов
        const hasMoreChats = (result.data?.length ?? 0) >= limit;
        setHasMore(hasMoreChats);
        hasMoreRef.current = hasMoreChats;
      } else {
        console.error('Failed to fetch chats:', result.error);
      }
    } catch (error) {
      console.error('Error fetching chats:', error);
      notify.error('Ошибка загрузки чатов');
    } finally {
      setIsLoading(false);
    }
  }, [options.limit, options.offset, options.search, options.linkedToOrder, options.chatType]);

  /**
   * Загрузить больше чатов (для infinite scroll)
   * Использует refs для предотвращения гонки условий
   */
  const fetchMoreChats = useCallback(async (): Promise<boolean> => {
    // Используем refs для проверки — они всегда актуальны
    if (isLoadingMoreRef.current || !hasMoreRef.current) {return false;}

    // Блокируем повторные вызовы через ref
    isLoadingMoreRef.current = true;
    setIsLoadingMore(true);

    try {
      const limit = options.limit ?? CHATS_PER_PAGE;
      // Используем ref для актуального значения offset
      const offset = chatsLengthRef.current;
      const queryString = new URLSearchParams({
        limit: String(limit),
        offset: String(offset),
        ...(options.search && { search: options.search }),
        ...(options.linkedToOrder !== undefined && { linkedToOrder: String(options.linkedToOrder) }),
        ...(options.chatType && { chatType: options.chatType }),
      }).toString();

      const response = await fetch(`${API_URL}/api/telegram-client/chats?${queryString}`);

      const result: ApiResponse<TelegramChat[]> = await response.json();
      if (result.success && result.data) {
        // Фильтруем на клиенте если бэкенд не поддерживает chatType
        const filteredChats = options.chatType
          ? result.data?.filter(c => c.chatType === options.chatType) ?? []
          : result.data ?? [];
        // Добавляем в конец списка с дедупликацией по telegramChatId
        setChats(prev => {
          const existingIds = new Set(prev.map(c => c.telegramChatId));
          const newChats = filteredChats.filter(c => !existingIds.has(c.telegramChatId));
          return [...prev, ...newChats];
        });
        // Если получили меньше чем запрашивали — больше нет чатов
        const hasMoreChats = (result.data?.length ?? 0) >= limit;
        setHasMore(hasMoreChats);
        hasMoreRef.current = hasMoreChats;
        return filteredChats.length > 0;
      }
      return false;
    } catch {
      return false;
    } finally {
      isLoadingMoreRef.current = false;
      setIsLoadingMore(false);
    }
  }, [options.limit, options.search, options.linkedToOrder, options.chatType]);

  const fetchPublicMessages = useCallback(async () => {
    setIsPublicLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/telegram-client/public-messages`);

      // Тип ответа от API (raw data from backend)
      interface ApiPublicMessage {
        id: number;
        chatId: number;
        text: string | null;
        fromId?: number | null;
        fromName: string | null;
        telegramDate: string;
        markedPublicByAdminId: number;
        markedPublicAt: string | null;
        chat?: {
          id: number;
          title: string | null;
        };
        markedByAdmin?: {
          id: number;
          username: string;
        } | null;
      }

      const result: ApiResponse<ApiPublicMessage[]> = await response.json();
      if (result.success && result.data) {
        // Маппим данные в формат PublicMessage
        const mappedMessages: PublicMessage[] = (result.data ?? []).map(msg => ({
          id: msg.id,
          chatId: msg.chatId,
          text: msg.text,
          fromId: msg.fromId ?? 0,
          fromName: msg.fromName,
          telegramDate: msg.telegramDate,
          markedByAdminId: msg.markedPublicByAdminId,
          markedByAdminName: msg.markedByAdmin?.username ?? null,
          markedPublicAt: msg.markedPublicAt,
          linkedOrderId: null, // TODO: добавить если нужно
          chatTitle: msg.chat?.title ?? null,
        }));
        setPublicMessages(mappedMessages);
      }
    } catch {
      // ignore
    } finally {
      setIsPublicLoading(false);
    }
  }, []);

  // НЕ запускаем fetchChats() автоматически при монтировании —
  // загрузку чатов контролирует TelegramClientPage через refetchChats(),
  // который вызывается только после подтверждения isAuthorized.
  // Автозапуск вызывал race condition: чаты запрашивались до готовности GramJS сессии.

  const linkChatToOrder = useCallback(async (chatId: number, orderId: number): Promise<boolean> => {
    setIsLinking(true);
    try {
      const response = await fetch(`${API_URL}/api/telegram-client/chats/${chatId}/link`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ orderId }),
      });

      const result: ApiResponse<null> = await response.json();

      if (!result.success) {
        notify.error(result.error || 'Ошибка при привязке чата');
        return false;
      }

      notify.success('Чат привязан к заказу');
      await fetchChats();
      return true;
    } catch {
      notify.error('Ошибка сети');
      return false;
    } finally {
      setIsLinking(false);
    }
  }, [fetchChats]);

  const unlinkChatFromOrder = useCallback(async (chatId: number, orderId: number): Promise<boolean> => {
    setIsLinking(true);
    try {
      const response = await fetch(`${API_URL}/api/telegram-client/chats/${chatId}/link/${orderId}`, {
        method: 'DELETE',
      });

      const result: ApiResponse<null> = await response.json();

      if (!result.success) {
        notify.error(result.error || 'Ошибка при отвязке чата');
        return false;
      }

      notify.success('Чат отвязан от заказа');
      await fetchChats();
      return true;
    } catch {
      notify.error('Ошибка сети');
      return false;
    } finally {
      setIsLinking(false);
    }
  }, [fetchChats]);

  /**
   * Обновить данные чата локально (например, обнулить unreadCount)
   * Только обновляет поля, которые не undefined
   */
  const updateChat = useCallback((chatId: number, updates: Partial<TelegramChat>) => {
    setChats(prev => prev.map(chat => {
      if (chat.id !== chatId) {return chat;}

      // Фильтруем undefined значения чтобы не перезаписывать существующие данные
      const filteredUpdates = Object.fromEntries(
        Object.entries(updates).filter(([, value]) => value !== undefined)
      );

      return { ...chat, ...filteredUpdates };
    }));
  }, []);

  return {
    chats,
    publicMessages,
    isLoading,
    isLoadingMore,
    hasMore,
    isPublicLoading,
    isLinking,
    refetch: fetchChats,
    fetchMoreChats,
    refetchPublic: fetchPublicMessages,
    linkChatToOrder,
    unlinkChatFromOrder,
    updateChat,
  };
}
